<template>
  <RrForm class="ren-data-inspector" layout="vertical" size="small">
    <RrFormItem>
      <template #label>
        组件数据源
        <TemplateLockMark :locked="locked" />
      </template>
      <RrSelect
        :disabled="disabled || sourceOptions.length <= 1"
        :title="fieldTitle"
        :value="sourceCode"
        :options="sourceOptions"
        @update:value="(val) => emit('update:source', String(val))"
      />
    </RrFormItem>
    <RrCard v-if="activeSource" size="small" :title="activeSource.sourceName">
      <p>{{ activeSource.method }} {{ activeSource.endpoint }}</p>
      <p>{{ activeSource.description }}</p>
      <p>权限：{{ activeSource.requiredPermission || '-' }}</p>
    </RrCard>
    <RrFormItem>
      <template #label>
        数据源参数 JSON
        <TemplateLockMark :locked="locked" />
      </template>
      <RrInput
        type="textarea"
        :rows="4"
        :disabled="disabled || !sourceCode"
        :title="fieldTitle"
        :value="paramsText"
        @update:value="(val) => emit('update:params', String(val))"
      />
    </RrFormItem>
    <section v-if="sourceCode" class="ren-data-inspector__bindings">
      <header>
        <div>
          <strong>字段绑定关系 <TemplateLockMark :locked="locked" /></strong>
          <span>组件字段 ← 数据结果路径</span>
        </div>
        <RrButton size="small" type="primary" :disabled="disabled" :title="fieldTitle" @click="emit('add-binding')">
          添加绑定
        </RrButton>
      </header>
      <div v-if="bindingEntries.length" class="ren-data-inspector__list">
        <article v-for="([target, source], index) in bindingEntries" :key="`${target}-${index}`">
          <RrSelect
            :disabled="disabled"
            :title="fieldTitle"
            :value="target"
            :options="bindingOptions"
            @update:value="(val) => emit('rename-binding', target, String(val))"
          />
          <span>←</span>
          <RrInput
            :disabled="disabled"
            :title="fieldTitle"
            :value="source"
            placeholder="records[].title"
            @update:value="(val) => emit('update-binding', target, String(val))"
          />
          <RrButton
            size="small"
            type="primary" danger
            :disabled="disabled"
            aria-label="删除绑定"
            @click="emit('remove-binding', target)">
            <Trash2 :size="12" />
          </RrButton>
        </article>
      </div>
      <p v-else class="ren-data-inspector__hint">尚未配置字段绑定，客户端按组件默认字段读取。</p>
      <details v-if="activeSource" class="ren-decoration-json">
        <summary>数据样例</summary>
        <pre>{{ sampleJson }}</pre>
      </details>
    </section>
  </RrForm>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Trash2 } from '@lucide/vue'
import { RrButton, RrCard, RrForm, RrFormItem, RrInput, RrSelect, type SelectOption } from '@xjlv2/ui'
import type { RenDecorationDataSourceDefinition } from '../../types'
import TemplateLockMark from '../utils/TemplateLockMark'

const props = withDefaults(
  defineProps<{
    sourceCode?: string
    paramsText?: string
    bindings?: Record<string, string>
    sourceOptions?: SelectOption[]
    bindingOptions?: SelectOption[]
    activeSource?: RenDecorationDataSourceDefinition | null
    readonly?: boolean
    templateBound?: boolean
    canEdit?: boolean
  }>(),
  {
    sourceCode: '',
    paramsText: '',
    bindings: () => ({}),
    sourceOptions: () => [],
    bindingOptions: () => [],
    activeSource: null,
    readonly: false,
    templateBound: false,
    canEdit: true,
  },
)

const emit = defineEmits<{
  'update:source': [code: string]
  'update:params': [value: string]
  'add-binding': []
  'rename-binding': [current: string, next: string]
  'update-binding': [target: string, source: string]
  'remove-binding': [target: string]
}>()

const disabled = computed(() => props.readonly || !props.canEdit)
const locked = computed(() => props.templateBound && !props.canEdit)
const fieldTitle = computed(() => (locked.value ? '该字段由业务模板锁定' : ''))
const bindingEntries = computed(() => Object.entries(props.bindings))
const sampleJson = computed(() => {
  try {
    return JSON.stringify(JSON.parse(props.activeSource?.sampleJson || '{}'), null, 2)
  } catch {
    return props.activeSource?.sampleJson || '{}'
  }
})
</script>

<style scoped>
.ren-data-inspector {
  display: grid;
  gap: var(--rr-space-3);
}

.ren-data-inspector__bindings {
  display: grid;
  gap: var(--rr-space-3);
}

.ren-data-inspector__bindings header {
  display: flex;
  justify-content: space-between;
  gap: var(--rr-space-3);
  align-items: flex-start;
}

.ren-data-inspector__bindings span,
.ren-data-inspector__hint,
.ren-data-inspector p {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-xs, 12px);
}

.ren-data-inspector__list {
  display: grid;
  gap: var(--rr-space-2);
}

.ren-data-inspector__list article {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto;
  gap: var(--rr-space-2);
  align-items: center;
}

.ren-data-inspector :deep(.rr-form-item) {
  margin-bottom: 0;
}

.ren-data-inspector :deep(.rr-form-item__label-text) {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-1);
}
</style>
